/**
 * update_schedule Tool - Update a recurring test schedule
 */

import type { Tool } from '@modelcontextprotocol/sdk/types.js';
import type { JobClient } from '../clients/job-client.js';
import type { UpdateJobScheduleRequest } from '@runhuman/shared';
import { FREQUENCY_LABELS } from '@runhuman/shared';

export interface UpdateScheduleArgs extends UpdateJobScheduleRequest {
  projectId: string;
  scheduleId: string;
}

export const updateScheduleToolDefinition: Tool = {
  name: 'update_schedule',
  description: `Update an existing schedule's settings.

Can change any combination of: name, template, frequency, time, timezone, weekdays, day of month, status, URL override.

Common uses:
- Pause a schedule: update_schedule(status: "paused")
- Resume a schedule: update_schedule(status: "active")
- Change time: update_schedule(scheduledHour: 14, scheduledMinute: 30)
- Change frequency: update_schedule(frequency: "daily")`,
  inputSchema: {
    type: 'object',
    properties: {
      projectId: {
        type: 'string',
        description: 'Project ID (required).',
      },
      scheduleId: {
        type: 'string',
        description: 'Schedule ID to update (required). Get from list_schedules.',
      },
      name: { type: 'string', description: 'New schedule name.' },
      templateId: { type: 'string', description: 'New template ID.' },
      frequency: {
        type: 'string',
        enum: ['daily', 'weekly', 'biweekly', 'monthly', 'once'],
        description: 'New frequency.',
      },
      scheduledHour: { type: 'number', description: 'New hour (0-23).', minimum: 0, maximum: 23 },
      scheduledMinute: { type: 'number', description: 'New minute (0, 15, 30, 45).', enum: [0, 15, 30, 45] },
      timezone: { type: 'string', description: 'New IANA timezone.' },
      weekdays: {
        type: 'array',
        items: { type: 'number', minimum: 0, maximum: 6 },
        description: 'New weekdays (0=Sun..6=Sat).',
      },
      dayOfMonth: { type: 'number', description: 'New day of month (1-28, 0=last).', minimum: 0, maximum: 28 },
      scheduledDate: { type: 'string', description: 'New date for once (YYYY-MM-DD).' },
      status: {
        type: 'string',
        enum: ['active', 'paused'],
        description: 'Set status to active or paused.',
      },
      urlOverride: { type: 'string', description: 'New URL override (null to clear).' },
      autoCreateGithubIssuesOverride: { type: 'boolean', description: 'Auto-create GitHub issues.' },
      autoCreateOnlyTesterSurfacedOverride: {
        type: 'boolean',
        description: 'Override the template default for filing only tester-surfaced issues.',
      },
      reopenIssuesOnDuplicateOverride: {
        type: 'boolean',
        description: 'Override the template default for reopening closed issues on a duplicate match.',
      },
      commentOnDuplicateOverride: {
        type: 'boolean',
        description: 'Override the template default for posting a cross-reference comment when a finding matches an existing issue.',
      },
    },
    required: ['projectId', 'scheduleId'],
  },
};

export async function handleUpdateSchedule(
  args: UpdateScheduleArgs,
  jobClient: JobClient,
): Promise<{ content: Array<{ type: 'text'; text: string }>; isError?: boolean }> {
  try {
    const { projectId, scheduleId, ...updateData } = args;
    const schedule = await jobClient.updateSchedule(projectId, scheduleId, updateData);

    const freq = FREQUENCY_LABELS[schedule.frequency];
    const time = `${String(schedule.scheduledHour).padStart(2, '0')}:${String(schedule.scheduledMinute).padStart(2, '0')}`;

    return {
      content: [{
        type: 'text',
        text: `Schedule Updated!

Name: ${schedule.name}
Schedule ID: ${schedule.id}
Frequency: ${freq}
Time: ${time} ${schedule.timezone}
Status: ${schedule.status}
Next Run: ${schedule.nextExecutionAt}`,
      }],
    };
  } catch (error) {
    const err = error as Error & { status?: number };
    return {
      content: [{
        type: 'text',
        text: `Failed to update schedule\n\nError: ${err.message}${err.status ? `\nStatus: ${err.status}` : ''}`,
      }],
      isError: true,
    };
  }
}
